import { PARTICLES } from '../constants';

interface Particle {
  alive: boolean;
  x: number;
  y: number;
  vx: number;
  vy: number;
  hue: number;
  /** 0..1 progress; 1 = freshly spawned. */
  life: number;
  /** Initial radius in world units. */
  r: number;
}

/**
 * Fixed-size particle pool. Allocation-free at runtime — spawning past the
 * pool ceiling just recycles the oldest slot. This is the right trade for
 * particle bursts where overflow is a sign of "too much juice" anyway.
 */
export class ParticleSystem {
  private readonly pool: Particle[];
  private cursor = 0;

  constructor() {
    this.pool = new Array(PARTICLES.POOL);
    for (let i = 0; i < this.pool.length; i++) {
      this.pool[i] = { alive: false, x: 0, y: 0, vx: 0, vy: 0, hue: 0, life: 0, r: 0 };
    }
  }

  /**
   * Spawn `count` particles in a radial burst from (x,y), tinted `hue`.
   * Each particle gets a random outward direction and speed within [0.6, 1.0]
   * of `speed`. Lifespan jitter ±25%.
   */
  burst(x: number, y: number, hue: number, count: number, speed: number): void {
    for (let i = 0; i < count; i++) {
      const p = this.pool[this.cursor]!;
      const ang = Math.random() * Math.PI * 2;
      const s = speed * (0.6 + Math.random() * 0.4);
      p.alive = true;
      p.x = x;
      p.y = y;
      p.vx = Math.cos(ang) * s;
      p.vy = Math.sin(ang) * s;
      p.hue = hue;
      p.life = 0.75 + Math.random() * 0.5; // 0.75 .. 1.25
      p.r =
        PARTICLES.RADIUS_MIN +
        Math.random() * (PARTICLES.RADIUS_MAX - PARTICLES.RADIUS_MIN);
      this.cursor = (this.cursor + 1) % this.pool.length;
    }
  }

  /** Advance particle simulation. */
  step(dt: number): void {
    const drag = Math.exp(-PARTICLES.DRAG * dt);
    const decay = dt / PARTICLES.LIFE;
    const pool = this.pool;
    for (let i = 0; i < pool.length; i++) {
      const p = pool[i]!;
      if (!p.alive) continue;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.vx *= drag;
      p.vy *= drag;
      p.life -= decay;
      if (p.life <= 0) p.alive = false;
    }
  }

  /** Iterator-style hand-off for the renderer (zero allocations). */
  forEachAlive(fn: (p: Readonly<Particle>) => void): void {
    const pool = this.pool;
    for (let i = 0; i < pool.length; i++) {
      const p = pool[i]!;
      if (p.alive) fn(p);
    }
  }
}

export type { Particle };
